import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Header from "@/components/landing-v2/Header";
import Footer from "@/components/landing-v2/Footer";
import { Calendar, ArrowRight, Video, MessageSquare } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" as const },
  }),
};

interface BlogPost {
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
  icon: React.ElementType;
  slug: string;
}

const BLOG_POSTS: BlogPost[] = [
  {
    title: "Hooki w social media: kompletny poradnik",
    excerpt:
      "Czym jest hook, dlaczego decyduje o wszystkim i jak pisać hooki, które zatrzymują scrollowanie? 10 formatów, kąty, emocje i złota formuła.",
    date: "9 kwi 2026",
    readTime: "15 min",
    category: "Strategia",
    icon: MessageSquare,
    slug: "hooki-w-social-media",
  },
  {
    title: "Jak zacząć nagrywać wideo do internetu? Kompletny poradnik",
    excerpt:
      "Jaki sprzęt wybrać na start, jak ustawić kadr, co zrobić z dźwiękiem i jak wyglądać przed kamerą — praktyczny przewodnik krok po kroku.",
    date: "18 lut 2026",
    readTime: "12 min",
    category: "Poradnik",
    icon: Video,
    slug: "jak-zaczac-nagrywac-wideo-do-internetu",
  },
];

const CATEGORY_COLORS: Record<string, string> = {
  Poradnik: "bg-blue-100 text-blue-700",
  Strategia: "bg-blue-100 text-blue-700",
};

const Blog = () => {
  return (
    <div className="min-h-screen text-gray-900">
      <Header />

      <section className="relative min-h-screen pt-28 pb-20">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F9B896] via-[#FABFA3] to-[#FCCAB0]" />

        <div className="relative z-10 max-w-4xl mx-auto px-4">
          {/* Hero */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
            className="text-center mb-14"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-4">
              Blog
            </h1>
            <p className="text-lg text-gray-700 max-w-lg mx-auto">
              Praktyczna wiedza o tworzeniu contentu, algorytmach i wzroście na social mediach
            </p>
          </motion.div>

          {/* Blog grid */}
          <div className="grid sm:grid-cols-2 gap-6">
            {BLOG_POSTS.map((post, i) => (
              <motion.article
                key={post.title}
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={i + 2}
                className="group"
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 p-6 flex flex-col justify-between gap-4 hover:shadow-lg transition-shadow h-full block cursor-pointer"
                >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-xs font-medium px-2.5 py-1 rounded-full ${CATEGORY_COLORS[post.category] ?? "bg-gray-100 text-gray-700"}`}
                    >
                      {post.category}
                    </span>
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <post.icon className="w-4 h-4 text-primary" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-base leading-snug">{post.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{post.excerpt}</p>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {post.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {post.readTime} czytania
                  </span>
                </div>
                </Link>
              </motion.article>
            ))}
          </div>

          {/* Newsletter CTA */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={8}
            className="mt-14 bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 p-8 text-center"
          >
            <h2 className="font-semibold text-xl mb-2">Nie przegap nowych artykulow</h2>
            <p className="text-sm text-gray-600 mb-1">
              Sledz nas na Instagramie, zeby byc na biezaco z nowymi tresciami.
            </p>
            <a
              href="https://instagram.com/viraluj.pl"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-primary font-medium text-sm hover:underline mt-3"
            >
              @viraluj.pl
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
